import { useState, useEffect } from 'react';
import registrationService from '../services/registration.service';
import { InvitationListView } from '@/features/email-invitations';
import type { ReferralStats } from '../types/referral.types';

export const ReferralLinkCard: React.FC = () => {
  const [referralLink, setReferralLink] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [activeTab, setActiveTab] = useState<'send' | 'history'>('send');

  // Email invitation states
  const [emailInput, setEmailInput] = useState<string>('');
  const [isSendingEmails, setIsSendingEmails] = useState<boolean>(false);
  const [emailSuccess, setEmailSuccess] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');

  // Fetch referral link and stats on component mount
  useEffect(() => {
    fetchReferralLink();
    fetchStats();
  }, []);

  const fetchReferralLink = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await registrationService.getReferralLink();
      setReferralLink(response.data.referralLink);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load referral link');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await registrationService.getReferralStats();
      setStats({
        totalRecruits: response.data.totalRecruits ?? 0,
        inOnboarding: response.data.inOnboarding ?? 0,
        activeAgents: response.data.activeAgents ?? 0,
        inactiveAgents: response.data.inactiveAgents ?? 0,
        terminatedAgents: response.data.terminatedAgents ?? 0,
      });
    } catch (err) {
      console.error('Failed to fetch referral stats:', err);
    }
  };

  const handleGenerateNewToken = async () => {
    try {
      setIsGenerating(true);
      setError('');
      await registrationService.generateReferralToken();
      await fetchReferralLink(); // Refresh the link after generating new token
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate new token');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleSendInvitations = async () => {
    if (!emailInput.trim()) {
      setEmailError('Please enter at least one email address');
      return;
    }

    // Parse emails (comma or newline separated)
    const emails = emailInput
      .split(/[,\n]/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email.length > 0);

    if (emails.length === 0) {
      setEmailError('Please enter valid email addresses');
      return;
    }

    try {
      setIsSendingEmails(true);
      setEmailError('');
      setEmailSuccess('');

      const response = await registrationService.sendInvitation(emails);

      if (response.data.sent > 0) {
        setEmailSuccess(`Successfully sent ${response.data.sent} invitation${response.data.sent > 1 ? 's' : ''}!`);
        setEmailInput(''); // Clear the input on success
      }

      if (response.data.failed > 0) {
        setEmailError(`Could not send to: ${response.data.errors.join(', ')}`);
      }

      // Clear success message after 5 seconds
      setTimeout(() => setEmailSuccess(''), 5000);
    } catch (err: any) {
      setEmailError(err.response?.data?.message || 'Failed to send invitation emails');
    } finally {
      setIsSendingEmails(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <svg
          className="w-6 h-6 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
        <h2 className="text-xl font-semibold text-gray-800">Your Referral Link</h2>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Referral Link Display */}
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-2">
          Share this link to invite new recruits:
        </p>
        <div className="flex gap-2">
          <div className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 font-mono text-sm text-gray-700 overflow-x-auto">
            {referralLink || 'No referral link available'}
          </div>
          <button
            onClick={handleCopyLink}
            disabled={!referralLink}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${copySuccess
              ? 'bg-green-600 text-white'
              : 'bg-blue-600 text-white hover:bg-blue-700'
              } disabled:bg-gray-300 disabled:cursor-not-allowed`}
          >
            {copySuccess ? (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Copied!
              </span>
            ) : (
              'Copy'
            )}
          </button>
          <button
            onClick={handleGenerateNewToken}
            disabled={isGenerating}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isGenerating ? 'Generating...' : 'Generate New'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Generate a new link if you want to invalidate the current one
        </p>
      </div>

      {/* Tabs for Send vs History */}
      <div className="border-t pt-6 mb-6">
        <div className="flex gap-4 mb-4 border-b">
          <button
            onClick={() => setActiveTab('send')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'send'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Send Invitations
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Invitation History
          </button>
        </div>

        {/* Send Tab Content */}
        {activeTab === 'send' && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h3 className="text-sm font-semibold text-gray-700">Send Invitation Emails</h3>
            </div>

        <p className="text-sm text-gray-600 mb-3">
          Enter email addresses to send personalized invitation emails with your referral link:
        </p>

        {/* Email Success Message */}
        {emailSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
            <p className="text-green-800 text-sm flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {emailSuccess}
            </p>
          </div>
        )}

        {/* Email Error Message */}
        {emailError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
            <p className="text-red-800 text-sm">{emailError}</p>
          </div>
        )}

        <div className="flex gap-2">
          <textarea
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              setEmailError('');
            }}
            placeholder="Enter email addresses (separate multiple with commas or new lines)&#10;e.g., john@example.com, jane@example.com"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
          />
        </div>
        <div className="flex justify-between items-center mt-3">
          <p className="text-xs text-gray-500">
            Separate multiple emails with commas or new lines
          </p>
          <button
            onClick={handleSendInvitations}
            disabled={isSendingEmails || !emailInput.trim()}
            className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSendingEmails ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Send Invitations
              </>
            )}
          </button>
        </div>
          </>
        )}

        {/* History Tab Content */}
        {activeTab === 'history' && (
          <div className="mt-4">
            <InvitationListView />
          </div>
        )}
      </div>

      {/* Referral Stats */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-3">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="text-sm font-semibold text-gray-700">Your Referral Stats</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">
              {stats ? stats.totalRecruits : (
                <span className="inline-block w-4 h-6 bg-gray-200 animate-pulse rounded"></span>
              )}
            </p>
            <p className="text-xs text-gray-600">Total Recruits</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {stats ? stats.inOnboarding : (
                <span className="inline-block w-4 h-6 bg-gray-200 animate-pulse rounded"></span>
              )}
            </p>
            <p className="text-xs text-gray-600">In Onboarding</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {stats ? stats.activeAgents : (
                <span className="inline-block w-4 h-6 bg-gray-200 animate-pulse rounded"></span>
              )}
            </p>
            <p className="text-xs text-gray-600">Active Agents</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-600">
              {stats ? stats.inactiveAgents : (
                <span className="inline-block w-4 h-6 bg-gray-200 animate-pulse rounded"></span>
              )}
            </p>
            <p className="text-xs text-gray-600">Inactive Agents</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">
              {stats ? stats.terminatedAgents : (
                <span className="inline-block w-4 h-6 bg-gray-200 animate-pulse rounded"></span>
              )}
            </p>
            <p className="text-xs text-gray-600">Terminated Agents</p>
          </div>
        </div>
        {!stats && (
          <p className="text-xs text-gray-500 text-center mt-3 animate-pulse">
            Loading statistics...
          </p>
        )}
      </div>
    </div>
  );
};
